import React, { useState } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { t } from '../../i18n';
import { Language } from '../../types';

interface PredictionData {
  predictions: {
    winner: {
      id: number;
      name: string;
      comment: string | null;
    };
    win_or_draw: boolean;
    under_over: string | null;
    goals: {
      home: string | null;
      away: string | null;
    };
    advice: string;
    percent: {
      home: string;
      draw: string;
      away: string;
    };
  };
  comparison: {
    form: { home: string; away: string };
    att: { home: string; away: string };
    def: { home: string; away: string };
    poisson_distribution: { home: string; away: string };
    h2h: { home: string; away: string };
    goals: { home: string; away: string };
    total: { home: string; away: string };
  };
}

interface MatchPredictionGraphProps {
  data: PredictionData;
  lang: Language;
}

export const MatchPredictionGraph: React.FC<MatchPredictionGraphProps> = ({
  data,
  lang,
}) => {
  const { comparison, predictions } = data;

  const stats = [
    { label: t(lang, 'strength'), home: comparison.total.home, away: comparison.total.away },
    { label: t(lang, 'attackingPotential'), home: comparison.att.home, away: comparison.att.away },
    { label: t(lang, 'defensivePotential'), home: comparison.def.home, away: comparison.def.away },
    { label: t(lang, 'poissonDistribution'), home: comparison.poisson_distribution.home, away: comparison.poisson_distribution.away },
    { label: t(lang, 'strengthH2h'), home: comparison.h2h.home, away: comparison.h2h.away },
    { label: t(lang, 'goalsH2h'), home: comparison.goals.home, away: comparison.goals.away },
    { label: t(lang, 'winProbability'), home: predictions.percent.home, away: predictions.percent.away },
  ];

  const [viewType, setViewType] = useState<'bar' | 'radar'>('radar');

  const parsePercent = (val: string) => parseInt(val.replace('%', '')) || 0;

  const radarData = stats.map(stat => ({
    subject: stat.label,
    A: parsePercent(stat.home),
    B: parsePercent(stat.away),
    fullMark: 100,
  }));

  return (
    <div className="px-4 md:px-0 space-y-4">
      <div className="flex justify-end -mt-14 mb-4 relative z-10">
        <div className="bg-slate-100 p-1 rounded-lg flex items-center shadow-inner">
          <button
            onClick={() => setViewType('bar')}
            className={`px-3 py-1.5 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${
              viewType === 'bar' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {t(lang, 'barView') || 'Bar'}
          </button>
          <button
            onClick={() => setViewType('radar')}
            className={`px-3 py-1.5 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${
              viewType === 'radar' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {t(lang, 'radarView') || 'Radar'}
          </button>
        </div>
      </div>

      {viewType === 'bar' ? (
        stats.map((stat, idx) => {
        const homeVal = parsePercent(stat.home);
        const awayVal = parsePercent(stat.away);
        
        return (
          <div key={idx} className="flex flex-col items-center gap-1.5">
            <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-tighter">
              {stat.label}
            </h3>
            <div className="w-full flex items-center gap-4 md:gap-8">
              <span className="text-[12px] font-bold text-slate-500 w-10 text-right tabular-nums">
                {stat.home}
              </span>
              <div className="flex-1 h-2 flex items-center relative">
                <div className="absolute inset-0 bg-slate-50 rounded-full" />
                <div 
                  className="h-full bg-rose-500 rounded-l-full relative z-10 shadow-[0_0_8px_rgba(244,63,94,0.3)]"
                  style={{ 
                    width: `${homeVal / 2}%`, 
                    marginLeft: `${50 - (homeVal / 2)}%` 
                  }}
                />
                <div 
                  className="h-full bg-slate-900 rounded-r-full relative z-10 shadow-[0_0_8px_rgba(15,23,42,0.2)]"
                  style={{ 
                    width: `${awayVal / 2}%`
                  }}
                />
              </div>
              <span className="text-[12px] font-bold text-slate-500 w-10 text-left tabular-nums">
                {stat.away}
              </span>
            </div>
          </div>
        );
      })
      ) : (
        <div className="w-full h-[300px] flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
              <PolarGrid stroke="#f1f5f9" />
              <PolarAngleAxis 
                dataKey="subject" 
                tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
              />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
              <Radar
                name="Home"
                dataKey="A"
                stroke="#f43f5e"
                fill="#f43f5e"
                fillOpacity={0.4}
              />
              <Radar
                name="Away"
                dataKey="B"
                stroke="#0f172a"
                fill="#0f172a"
                fillOpacity={0.4}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
